/**
 * The shape every marketing FAQ answer is authored in.
 *
 * The published answers are rich text, not lines of prose: the link sits inside
 * the sentence that offers it, a phrase is emphasised where it is read, and the
 * setup steps are a real list. A string per line carries none of that, and the
 * migration that flattened these answers to strings is what lost the site's
 * inline anchors, all of its inline bold and half of its list items.
 *
 * So an answer is a list of blocks and a block is a list of runs, the same way
 * `@/content/legalPages` already models the legal copy. The feature pages, the
 * location pages and the landing pages all author answers in this one shape.
 */

/**
 * A stretch of answer text. `href` makes it an anchor, `bold` emphasises it,
 * and both together is published: two answers anchor an already-bold phrase.
 */
export type FaqRun = {
  text: string;
  bold?: true;
  href?: string;
  /** Only where the published anchor opens a new tab. */
  newTab?: true;
};

/** One printed line: its own paragraph, or one item of the list it belongs to. */
type FaqAnswerBlock =
  | { kind: "paragraph"; runs: Array<FaqRun> }
  | { kind: "item"; ordered?: true; runs: Array<FaqRun> };

/** A published answer, in the order it prints. */
export type FaqAnswer = Array<FaqAnswerBlock>;

/**
 * A paragraph, or the run of neighbouring items that share one list element.
 *
 * Authoring keeps items flat, because that is how they read and how the
 * published markup counts them, but `ul`/`ol` have to wrap theirs.
 */
type FaqAnswerGroup =
  | { kind: "paragraph"; runs: Array<FaqRun> }
  | { kind: "list"; ordered: boolean; items: Array<Array<FaqRun>> };

/** Wraps each run of adjacent items of like kind, so a renderer can emit lists. */
export function groupFaqAnswer(answer: FaqAnswer): Array<FaqAnswerGroup> {
  const groups: Array<FaqAnswerGroup> = [];

  for (const block of answer) {
    if (block.kind === "paragraph") {
      groups.push(block);
      continue;
    }
    const ordered = block.ordered === true;
    const last = groups.at(-1);
    // A numbered list next to a bulleted one is two lists, not one mixed one.
    if (last?.kind === "list" && last.ordered === ordered) {
      last.items.push(block.runs);
    } else {
      groups.push({ kind: "list", ordered, items: [block.runs] });
    }
  }

  return groups;
}

/**
 * An answer as one string, for `schema.org`'s `Answer.text`, which takes no
 * markup. Blocks are separated because they are separate lines on the page;
 * runs are not, because they are one continuous sentence.
 */
export function faqAnswerText(answer: FaqAnswer): string {
  return answer
    .map((block) => block.runs.map((run) => run.text).join(""))
    .join(" ")
    .trim();
}
